import { getOwner } from "@ember/owner";
import FormAction from "parog-web/components/forc/form-action";
import type Patient from "parog-web/models/patient";
import type { EmberChangeset } from "ember-changeset";
import type { Snapshot } from "ember-changeset/types";
import jsonParseWithDates from "parog-web/utils/json-parse-with-dates";
import {
  getItemFromLocalStorage,
  isLocalStorageAvailable,
  removeItemFromLocalStorage,
  setItemInLocalStorage
} from "parog-web/utils/use-local-storage";
/**
 * This class encapsulates the Auto Save Draft feature.
 *
 * It automatically saves content of a changeset in the local storage until
 * the JE is created.
 *
 * It's currently tightly coupled with ForcJournalEventBasedComponent, though
 * it's linked through an expected interface.
 *
 * The ForcJournalEventBasedComponent calls methods on DraftManager, as if
 * DraftManager was hooked to it (we could implement a real hook mechanism in
 * ForcJournalEventBasedComponent making it completely unaware of DraftManager
 * but it's currently not needed).
 *
 * When the content of the changeset is changed, the content is saved in the
 * local storage under a key computed with `localStorageKey`. The content is the
 * return of `Changeset.snapshot`, which contains changes and errors.
 * When the form is loaded, if a draft is present in the local storage, it is
 * restored in the changeset using `Changeset.restore`.
 *
 * It is important to keep in mind that the underlying model is never touched,
 * only the changeset is.
 *
 * Hook methods are run only if draft is supported (local storage is available,
 * a component id has been set, etc. This is checked in `_ensureAutoSavedDraftCanWork`).
 *
 * DraftManager could be refactored to only be dependent of Changeset if needed.
 *
 */

interface Form {
  autoSaveDraft: boolean;
  formAction: string;
  patient: Patient;
  changeset: EmberChangeset;
  fullComponentId: string;
  isDestroyed: boolean;
}

interface SerializedDraft {
  type: string;
  version: string;
  payload: {
    changesetSnapshot: Snapshot;
  };
}

export default class DraftManager {
  static hasDraftFor(key) {
    return Boolean(getItemFromLocalStorage(key));
  }

  static localStorageKey(componentId: string, patient: Patient) {
    return `${componentId}/patient_${patient.id}`;
  }

  form: Form;

  constructor(form: Form) {
    this.form = form;
  }

  async load() {
    if (!this._ensureAutoSavedDraftCanWork()) {
      return;
    }

    const draft = this._existingDraft();
    if (draft && draft.type == "draft" && draft.version == "1") {
      this.form.changeset.restore(draft.payload.changesetSnapshot);
    }
  }

  async onDidUpdate() {
    if (!this._ensureAutoSavedDraftCanWork()) {
      return;
    }

    // Remove patient from changeset snapshot
    const changesetSnapshot = this.form.changeset.snapshot();
    delete changesetSnapshot.changes.patient;

    // Remove files frome changeset snapshot
    Object.entries(changesetSnapshot.changes)
      // Find keys that contains File
      .forEach(([key, val]) => {
        if (
          val instanceof Array &&
          val.some(v => v.fileAttachment || v.fileContent)
        ) {
          delete changesetSnapshot.changes[key as any as string];
        }
      });

    const serializedDraft: SerializedDraft = {
      type: "draft",
      version: "1",
      payload: {
        changesetSnapshot
      }
    };
    setItemInLocalStorage(
      this._localStorageKey,
      JSON.stringify(serializedDraft)
    );
  }

  async clear() {
    if (!this._ensureAutoSavedDraftCanWork()) {
      return;
    }

    removeItemFromLocalStorage(this._localStorageKey);
  }

  async onDidSave() {
    await this.clear();
  }

  _ensureAutoSavedDraftCanWork() {
    // Fixes an issue since Ember 3.20, in tests at least, where this method may be called after the component is destroyed.
    if (this.form.isDestroyed) return;

    if (this.form.formAction != FormAction.New || !this.form.autoSaveDraft) {
      return false;
    }

    // LocalStorage may not be available, for instance when using
    // Private Browsing on Safari.
    if (!isLocalStorageAvailable) {
      console.log(
        "LocalStorage is not available. `autoSaveDraft` is automatically disabled."
      );
      return false;
    }

    if (!this.form.fullComponentId) {
      console.error(
        "You enabled `autoSaveDraft` but did not set the `fullComponentId`. It must be set for `autoSaveDraft` to work."
      );
      return false;
    }

    const owner = getOwner(this.form) ?? {};
    const forcComponents = owner["forcComponents"];

    if (!forcComponents[this.form.fullComponentId]) {
      console.error(
        `You set '${this.form.fullComponentId}' as the form's 'fullComponentId', but it has not been registered as a FORC component in the application's instance. 'fullComponentId' must be present as a key in the app's FORC components list.`
      );
      return false;
    }

    return true;
  }

  get _localStorageKey() {
    return DraftManager.localStorageKey(
      this.form.fullComponentId,
      this.form.patient
    );
  }

  /**
   * Parses the serialized draft in local storage.
   * Attempts to rehydrate dates by searching "date like" strings.
   *
   */
  _existingDraft(): SerializedDraft | undefined {
    const draftString = getItemFromLocalStorage(this._localStorageKey);

    if (!draftString) {
      return;
    }

    const draft = jsonParseWithDates(draftString) as SerializedDraft;

    return draft;
  }
}
